import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  cardDistanceOrigin,
  getDefaultSearchOrigin,
  isOriginInUse,
  searchedPlaceOrigin,
} from '../../util/search';
import { types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { ModalInMobile, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  initialValues,
  validUrlQueryParamsFromProps,
  validFilterParams,
  getSearchPageResourceLocatorStringParams,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import ListMapToggle from './ListMapToggle/ListMapToggle';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import CurrentLocationToggle from './CurrentLocationToggle/CurrentLocationToggle';
import SearchFiltersRail from './SearchFiltersRail/SearchFiltersRail';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
// Below this the map lives in a full-screen modal (opened by the List|Map toggle); from here up it's
// the inline right-hand column. Matches --viewportLarge.
const MAP_MODAL_BREAKPOINT = 1024;
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before a map-move search is initiated.

const { LatLng, LatLngBounds } = sdkTypes;
// Default map viewport when the URL carries no bounds (e.g. landing straight on /s): the LA + OC
// service area (the only launch regions). Display-only -- it never enters the URL or the listing
// query. A module-level constant so the value is identical every render: the map's refit guard
// compares bounds by value, and a value that changes across renders re-fits the map, which fires
// idle events that 'search as map moves' turns into new searches.
const DEFAULT_MAP_BOUNDS = new LatLngBounds(
  new LatLng(34.35, -117.4), // NE: north of LA metro, east of Orange County
  new LatLng(33.35, -118.75) // SW: south of OC coast, west of LA coast
);

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      isSearchMapOpenOnMobile: false,
      // Bumped once the map provider's JS lib has loaded. SearchMap renders an empty placeholder
      // until the lib is available, and nothing else re-renders on script load (without SSR the
      // script usually finishes AFTER the first client render) -- this tick re-renders us so the
      // real map mounts.
      mapLibReadyTick: 0,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      // The parent's current location, when they turned it on (CurrentLocationToggle).
      deviceOrigin: null,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.applyFilterParams = this.applyFilterParams.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  // KidHive: merge a rail-filter patch into the current URL params and push (null/empty deletes the
  // key; page resets so filtered results start from page 1). The rail's custom filters (category, age
  // overlap, distance bounds, days) all funnel through here.
  applyFilterParams(patch) {
    const { history, routeConfiguration, location } = this.props;
    // Distance filter: the patch carries a `bounds` radius box. Pushing it makes the map re-fit to
    // that box, which fires onMapMoveEnd -> that would overwrite the exact radius box with the map's
    // (padded) viewport bounds and uncheck the distance option. Flag the next programmatic move so
    // onMapMoveEnd skips it once and the chosen radius sticks.
    if (patch && patch.bounds != null) {
      this.skipNextMapMoveEnd = true;
    }
    const merged = { ...parse(location.search), ...patch };
    const cleaned = Object.entries(merged).reduce(
      (acc, [key, value]) => (value == null || value === '' ? acc : { ...acc, [key]: value }),
      {}
    );
    delete cleaned.page;
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, cleaned));
  }

  componentDidMount() {
    // Wait for the map provider's JS lib (loaded via an injected <script>), then re-render once so
    // SearchMap swaps its empty placeholder for the real map. Bounded: gives up after ~15s.
    const mapProvider = this.props.config?.maps?.mapProvider;
    const isMapLibLoaded = () =>
      typeof window !== 'undefined' &&
      (mapProvider === 'googleMaps' ? !!window.google?.maps : !!window.mapboxgl);
    if (!isMapLibLoaded()) {
      let attempts = 0;
      this.mapLibPoll = window.setInterval(() => {
        attempts += 1;
        if (isMapLibLoaded() || attempts > 75) {
          window.clearInterval(this.mapLibPoll);
          this.mapLibPoll = null;
          if (isMapLibLoaded()) {
            this.setState(prev => ({ mapLibReadyTick: prev.mapLibReadyTick + 1 }));
          }
        }
      }, 200);
    }
  }

  componentWillUnmount() {
    if (this.mapLibPoll) {
      window.clearInterval(this.mapLibPoll);
    }
  }

  // New search when the user pans/zooms the map ("search as map moves", like the map variant).
  // Debounced; fires only on real viewport changes and only while on the search page (the reusable
  // map container can emit from other pages).
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;
    const { params: currentPathParams } = this.props;

    const routes = this.props.routeConfiguration;
    const searchPagePath = currentPathParams.listingType
      ? pathByRouteName('SearchPageWithListingType', routes, currentPathParams)
      : pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;
    const isSearchPage = currentPath === searchPagePath;

    if (viewportBoundsChanged && isSearchPage) {
      // Skip the single programmatic re-fit that follows a distance-filter change, so the radius box
      // set by the filter isn't immediately overwritten by the map's padded viewport bounds.
      if (this.skipNextMapMoveEnd) {
        this.skipNextMapMoveEnd = false;
        return;
      }
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      // Parse the current query; a map-move search resets pagination.
      const { address, bounds, mapSearch, page, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routes, location);

      history.push(createResourceLocatorString(routeName, routes, pathParams, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
      activeListingId,
      onActivateListing,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      searchParamsInURL,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // The map: an inline right-hand column from the large viewport up; below that a full-screen
    // modal opened with the List|Map toggle (same UX as the map variant). The reusable map only
    // mounts when it can be seen.
    const { bounds, origin: searchOrigin } = searchParamsInURL || {};
    const isWindowDefined = typeof window !== 'undefined';
    const isMapModalLayout = isWindowDefined && window.innerWidth < MAP_MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMapModalLayout || (isMapModalLayout && this.state.isSearchMapOpenOnMobile);

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        {/* KidHive: one centered content container (landing-section rhythm) -- rail left, grid right. */}
        <div className={css.gridLayoutContainer}>
          <aside className={css.gridFilterColumn} data-testid="filterColumnAside">
            <div>
              <SearchFiltersRail
                // KidHive categories are FLAT and multi-selectable (the API accepts several
                // categoryLevel1 values), so the rail renders its OWN multi-checkbox category card and
                // we drop the built-in single-select category tree from the config list below.
                categories={listingCategories}
                // RAW parsed params, not validQueryParams: the shared validator drops params that
                // aren't configured filters (pub_scheduleDays, the ageBand overlap params) and leaves
                // bounds unparsed -- the rail's selected states derive from these.
                urlQueryParams={parse(location.search, {
                  latlng: ['origin'],
                  latlngBounds: ['bounds'],
                })}
                origin={parse(location.search, { latlng: ['origin'] }).origin}
                onApplyParams={this.applyFilterParams}
                onResetAll={e => this.handleResetAll(e)}
              >
                {availableFilters
                  .filter(f => f.schemaType !== 'category')
                  .map(filterConfig => {
                  const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      className={css.filter}
                      config={filterConfig}
                      containerId="SearchPageWithGrid_DesktopFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      isDesktop
                    />
                  );
                })}
              </SearchFiltersRail>
            </div>
          </aside>

          <div id="main-content" className={css.gridMain} role="main">
            <div>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                {/* KidHive: the mobile modal hosts the SAME rail as the desktop column -- custom cards
                    (category/age/distance/days) + config-driven filters, one implementation. */}
                <SearchFiltersRail
                  // Multi-checkbox category card (see the desktop rail note); drop the built-in
                  // single-select category tree below.
                  categories={listingCategories}
                  urlQueryParams={parse(location.search, {
                    latlng: ['origin'],
                    latlngBounds: ['bounds'],
                  })}
                  origin={parse(location.search, { latlng: ['origin'] }).origin}
                  onApplyParams={this.applyFilterParams}
                  onResetAll={e => this.handleResetAll(e)}
                >
                  {availableFilters
                    .filter(f => f.schemaType !== 'category')
                    .map(filterConfig => {
                    const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                    return (
                      <FilterComponent
                        key={key}
                        id={filterId}
                        config={filterConfig}
                        containerId="SearchPage_MobileFilters"
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersRail>
              </SearchFiltersMobile>
              {searchListingsError ? (
                // A failed search shows ONLY the quiet error message in the results panel.
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
              ) : (
                <>
                  {/* KidHive: the results heading -- the active category's name, or the browse-all title. */}
                  <h1 className={css.resultsHeading}>
                    {listingCategories?.find(c => c.id === validQueryParams.pub_categoryLevel1)
                      ?.name || <FormattedMessage id="SearchPage.allClassesHeading" />}
                  </h1>
                  {listingsAreLoaded && totalItems === 0 ? (
                    // No results: just the message -- no "0 results" count row, no sort dropdown, and
                    // no empty listings-grid scaffold.
                    noResultsInfo
                  ) : (
                    <>
                      <MainPanelHeader
                        className={classNames(css.mainPanel, css.gridMainPanelHeader)}
                        sortByComponent={sortBy('desktop')}
                        isSortByActive={sortConfig.active}
                        listingsAreLoaded={listingsAreLoaded}
                        resultsCount={totalItems}
                        searchInProgress={searchInProgress}
                        searchListingsError={searchListingsError}
                        noResultsInfo={noResultsInfo}
                      />
                      <div
                        className={classNames(css.listingsForGridVariant, {
                          [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                        })}
                      >
                        <SearchErrors
                          searchListingsError={searchListingsError}
                          isValidDatesFilter={isValidDatesFilter}
                        />
                        <CurrentLocationToggle
                          hasHomeLocation={!!getDefaultSearchOrigin(this.props.currentUser)}
                          searchedAddress={
                            searchedPlaceOrigin(parse(location.search, { latlng: ['origin'] }))
                              ?.address || null
                          }
                          onChange={deviceOrigin => this.setState({ deviceOrigin })}
                        />
                        <SearchResultsPanel
                          className={css.searchListingsPanel}
                          listings={listings}
                          pagination={listingsAreLoaded ? pagination : null}
                          search={parse(location.search)}
                          // Cards' distance origin: current location (if on) > saved home > the
                          // URL's origin (the searched place / map centre) as a fallback.
                          origin={cardDistanceOrigin({
                            searchedOrigin: searchedPlaceOrigin(
                              parse(location.search, { latlng: ['origin'] })
                            ),
                            deviceOrigin: this.state.deviceOrigin,
                            currentUser: this.props.currentUser,
                            urlOrigin: parse(location.search, { latlng: ['origin'] }).origin,
                          })}
                          setActiveListing={onActivateListing}
                          isMapVariant={false}
                          listingTypeParam={listingTypePathParam}
                          intl={intl}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* The Yelp-style map column: inline + sticky from the large viewport, a full-screen modal
              (via the List|Map toggle) below it. The column flexes to absorb ALL extra viewport
              width -- the rail and results stay put on bigger screens. */}
          <ModalInMobile
            className={css.gridMapColumn}
            id="SearchPageWithGrid_map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MAP_MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.gridMapWrapper} data-testid="searchMapContainer">
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.gridMap}
                  rootClassName={css.mapRoot}
                  activeListingId={activeListingId}
                  bounds={bounds || DEFAULT_MAP_BOUNDS}
                  center={searchOrigin}
                  isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                  location={location}
                  listings={listings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPageWithGrid_map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
        </div>

        {/* Below the large viewport: the segmented List | Map toggle opens the full-screen map. */}
        <ListMapToggle
          active={this.state.isSearchMapOpenOnMobile ? 'map' : 'list'}
          onSelect={view => this.setState({ isSearchMapOpenOnMobile: view === 'map' })}
        />
        {/* No app footer here (client): the search canvas is the rail|results|map workspace. */}
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const activeListingId = useSelector(state => state.SearchPage.activeListingId);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  // Card hover -> highlighted pin on the map.
  const onActivateListing = useCallback(listingId => dispatch(setActiveListing(listingId)), [
    dispatch,
  ]);

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      activeListingId={activeListingId}
      onActivateListing={onActivateListing}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );
};

export default SearchPage;
